import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { format, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import {
  getTimeOffCandidates,
  getTimeOffDetail,
  saveTimeOffClasses,
  submitTimeOff,
  clearSub,
  clearDetail,
  getMyTimeOff,
  TimeOffCandidate,
  TimeOffEvent,
} from '@/store/slices/timeOffSlice';
import { ScheduleStackParamList } from '@/types/navigation';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';
import SubPickerModal from '@/components/timeoff/SubPickerModal';
import { isTentativeDate } from '@/utils/scheduleWeek';
import { subActionFor, wasRecordedAfter } from '@/utils/timeOffSubRules';

interface ChipStyle {
  label: string;
  color: string;
  bg: string;
}

const shortName = (first?: string, last?: string): string =>
  `${(first ?? '').trim()}${last?.trim() ? ` ${last.trim()[0]}.` : ''}`.trim();

/**
 * Coverage chip driven purely by the server-derived `coverageState`. An accepted sub
 * whose accept stamp is dated after the class was recorded retroactively by a
 * manager (late-subs), so it reads "Recorded"; an ordinary pre-class accept stays
 * "Accepted" even once the class is in the past.
 */
const coverageChip = (event: TimeOffEvent): ChipStyle => {
  switch (event.coverageState) {
    case 'accepted': {
      const sub = event.sub?.employee;
      if (sub && wasRecordedAfter(event.sub?.acceptedOn, event.eventDate)) {
        return {
          label: `Recorded · ${shortName(sub.firstName, sub.lastName)}`,
          color: colors.court,
          bg: colors.courtSoft,
        };
      }
      return { label: 'Accepted', color: colors.court, bg: colors.courtSoft };
    }
    case 'assigned': {
      const sub = event.sub?.employee;
      const name = sub ? `Sub: ${sub.firstName} ${sub.lastName}` : 'Sub assigned';
      return { label: name, color: colors.court, bg: colors.courtSoft };
    }
    case 'unassigned':
    default:
      return { label: 'Needs sub', color: colors.warn, bg: colors.warnSoft };
  }
};

const fmtRange = (start: string, end: string): string => {
  try {
    const s = parseISO(start);
    const e = parseISO(end);
    if (start === end) {
      return format(s, 'EEE, MMM d');
    }
    return `${format(s, 'MMM d')} – ${format(e, 'MMM d')}`;
  } catch {
    return `${start} – ${end}`;
  }
};

const fmtEventDate = (date: string): string => {
  try {
    return format(parseISO(date), 'EEE, MMM d');
  } catch {
    return date;
  }
};

const fmtDayHeading = (date: string): string => {
  try {
    return format(parseISO(date), 'EEEE, MMM d');
  } catch {
    return date;
  }
};

const fmtShortDate = (date?: string | null): string | null => {
  if (!date) {
    return null;
  }
  try {
    return format(parseISO(date), 'MMM d');
  } catch {
    return null;
  }
};

/** "Declined by Dee C. on Oct 1 — needs a new sub", only for an uncovered class. */
export const declineLine = (event: TimeOffEvent): string | null => {
  const d = event.lastDecline;
  if (event.sub || !d?.employee) {
    return null;
  }
  const first = (d.employee.firstName ?? '').trim();
  const last = (d.employee.lastName ?? '').trim();
  const who = `${first}${last ? ` ${last[0]}.` : ''}`.trim() || 'Your sub';
  const when = fmtShortDate(d.respondedOn);
  return `Declined by ${who}${when ? ` on ${when}` : ''} — needs a new sub`;
};

/** Request statuses where subs can still be changed (the servlet 409s DENIED/CANCELLED). */
const EDITABLE_STATUSES = new Set(['CREATED', 'PENDING_APPROVAL', 'APPROVED']);

const fmtDay = (date: string): string => {
  try {
    return format(parseISO(date), 'EEE, MMM d');
  } catch {
    return date;
  }
};

const LOCKED_COPY: Record<string, string> = {
  past: 'Already passed',
  other_request: 'On another time-off request',
  has_sub: 'Has a sub — clear the sub to change',
};

/** A row the person can check/uncheck (not past, not someone else's, no sub on it). */
const canToggle = (c: TimeOffCandidate): boolean => c.selectable && !c.lockedReason;

/**
 * Starting checks: an existing request keeps what it already has selected; a
 * fresh draft (nothing selected yet) starts with every changeable class checked.
 */
export const initialChecks = (candidates: TimeOffCandidate[]): Set<number> => {
  const anySelected = candidates.some((c) => c.selected);
  return new Set(
    candidates.filter((c) => c.selected || (!anySelected && canToggle(c))).map((c) => c.id)
  );
};

/** What the person last asked for, kept across a reload so it can be re-applied. */
export interface ChecklistIntent {
  /** Ids they had checked. */
  checked: Set<number>;
  /** Every id the checklist showed; anything new after a reload starts checked. */
  known: Set<number>;
}

/**
 * Re-apply an intent to a FRESH candidate list: changeable rows follow the
 * person's choice (new rows default to checked, like a fresh draft); locked rows
 * keep their server state. Saving then only sends what is still different.
 */
export const applyIntent = (candidates: TimeOffCandidate[], intent: ChecklistIntent): Set<number> =>
  new Set(
    candidates
      .filter((c) =>
        canToggle(c) ? intent.checked.has(c.id) || !intent.known.has(c.id) : c.selected
      )
      .map((c) => c.id)
  );

/** POST rejections meaning the class ids are stale (the schedule was rebuilt). */
const STALE_REASONS = new Set(['not_found', 'not_eligible']);

/** Candidates grouped by day, keeping the server's date/start order. */
const groupByDay = (candidates: TimeOffCandidate[]) => {
  const groups: { date: string; items: TimeOffCandidate[] }[] = [];
  for (const c of candidates) {
    const last = groups[groups.length - 1];
    if (last && last.date === c.eventDate) {
      last.items.push(c);
    } else {
      groups.push({ date: c.eventDate, items: [c] });
    }
  }
  return groups;
};

interface ViewProps {
  requestId: number;
  readOnly?: boolean;
}

type Step = 'classes' | 'subs';

/**
 * Shared body for the assemble (editable) and detail (read-only) screens.
 *
 * Editable flow, two steps:
 *  1. classes — a day-grouped checklist of the requester's classes in the
 *     window (all checked by default): "Uncheck any class you'll still teach."
 *     Save POSTs newly checked / DELETEs newly unchecked classes.
 *  2. subs — the per-class sub picker for the checked classes, then submit.
 * Read-only shows step 2's rows without the edit affordances. Coverage is always
 * read from the server, never computed here.
 */
export const TimeOffAssembleView: React.FC<ViewProps> = ({ requestId, readOnly = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<any>();
  const reduxDetail = useSelector((state: RootState) => state.timeOff.detail);
  const reduxCandidates = useSelector((state: RootState) => state.timeOff.candidates);
  const { isSubmitting, isSavingClasses, error, detailErrorStatus } = useSelector(
    (state: RootState) => state.timeOff
  );
  const viewer = useSelector((state: RootState) => state.auth?.user?.employee);
  const viewerIsAdmin = !!viewer?.admin;

  // Guard against stale data from a previously-viewed request.
  const detail = reduxDetail && reduxDetail.id === requestId ? reduxDetail : null;
  const candidates =
    reduxCandidates && reduxCandidates.requestId === requestId ? reduxCandidates : null;

  const [step, setStep] = useState<Step>(readOnly ? 'subs' : 'classes');
  const [checked, setChecked] = useState<Set<number> | null>(null);
  /** Set before a reload that must keep the person's choices (failed save / refreshed schedule). */
  const [intent, setIntent] = useState<ChecklistIntent | null>(null);

  const [picker, setPicker] = useState<{
    visible: boolean;
    eventId: number;
    currentSubId: number | null;
    /** 'record' = a manager documenting a past class (late-subs). */
    mode: 'assign' | 'record';
    eventDate?: string;
  }>({ visible: false, eventId: 0, currentSubId: null, mode: 'assign' });

  const load = useCallback(() => {
    if (step === 'subs') {
      dispatch(getTimeOffDetail(requestId));
    } else {
      dispatch(getTimeOffCandidates(requestId));
    }
  }, [dispatch, requestId, step]);

  useEffect(() => {
    dispatch(clearDetail());
    if (readOnly) {
      dispatch(getTimeOffDetail(requestId));
    } else {
      dispatch(getTimeOffCandidates(requestId));
    }
    return () => {
      dispatch(clearDetail());
    };
  }, [dispatch, requestId, readOnly]);

  // Seed the checkboxes once per candidates load. `candidates` is cleared while a
  // reload is in flight, so this only ever sees the FRESH list.
  useEffect(() => {
    if (candidates && checked === null) {
      setChecked(
        intent ? applyIntent(candidates.candidates, intent) : initialChecks(candidates.candidates)
      );
      setIntent(null);
    }
  }, [candidates, checked, intent]);

  /** Reload the checklist from the server, keeping what the person asked for. */
  const reloadKeepingChoices = () => {
    const list = candidates?.candidates ?? [];
    setIntent({ checked: new Set(checked ?? []), known: new Set(list.map((c) => c.id)) });
    setChecked(null);
    dispatch(getTimeOffCandidates(requestId));
  };

  const groups = useMemo(() => groupByDay(candidates?.candidates ?? []), [candidates]);

  const toggle = (c: TimeOffCandidate) => {
    if (!canToggle(c)) {
      return;
    }
    setChecked((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(c.id)) {
        next.delete(c.id);
      } else {
        next.add(c.id);
      }
      return next;
    });
  };

  const finishSubmit = (noClasses: boolean) => {
    dispatch(submitTimeOff(noClasses ? { id: requestId, noClasses: true } : requestId)).then(
      (result) => {
        if (submitTimeOff.fulfilled.match(result) && (result.payload as any)?.success !== false) {
          dispatch(getMyTimeOff());
          navigation.navigate('TimeOff');
        } else {
          const msg = submitTimeOff.rejected.match(result)
            ? (result.payload as string)
            : (result.payload as any)?.message;
          Alert.alert('Time Off', msg || 'Could not submit the request.');
        }
      }
    );
  };

  /** Persist checklist changes. Resolves true when saved. */
  const saveClasses = async (): Promise<boolean> => {
    const list = candidates?.candidates ?? [];
    const marks = checked ?? new Set<number>();
    const add = list.filter((c) => canToggle(c) && !c.selected && marks.has(c.id)).map((c) => c.id);
    const remove = list
      .filter((c) => canToggle(c) && c.selected && !marks.has(c.id))
      .map((c) => c.id);
    const result = await dispatch(saveTimeOffClasses({ id: requestId, add, remove }));
    if (saveTimeOffClasses.rejected.match(result)) {
      // Part of the save may have gone through — reload so a retry only sends what's left.
      reloadKeepingChoices();
      Alert.alert('Time Off', (result.payload as string) || 'Could not save your classes.');
      return false;
    }
    const rejected = result.payload?.rejected ?? [];
    if (rejected.some((r) => STALE_REASONS.has(r.reason))) {
      // Class ids changed under us (the schedule was rebuilt): don't go on to subs.
      reloadKeepingChoices();
      Alert.alert('Time Off', 'The schedule was refreshed — review and save again.');
      return false;
    }
    if (rejected.length > 0) {
      Alert.alert(
        'Some classes were skipped',
        `${rejected.length} ${rejected.length === 1 ? 'class is' : 'classes are'} no longer available and ${rejected.length === 1 ? 'was' : 'were'} not added.`
      );
    }
    return true;
  };

  const onNext = async () => {
    if (await saveClasses()) {
      setStep('subs');
    }
  };

  /** Leave after editing a request that was already submitted (nothing to submit again). */
  const done = () => {
    dispatch(getMyTimeOff());
    navigation.navigate('TimeOff');
  };

  /**
   * Confirm before finishing with no classes needing a sub. Drafts then submit with
   * noClasses; an already-submitted request just saves (`then` = done).
   */
  const confirmNoClasses = (
    title: string,
    body: string,
    beforeSubmit?: () => Promise<boolean>,
    then: () => void = () => finishSubmit(true),
    confirmLabel = 'Submit'
  ) => {
    Alert.alert(title, body, [
      { text: 'Go back', style: 'cancel' },
      {
        text: confirmLabel,
        onPress: async () => {
          if (beforeSubmit && !(await beforeSubmit())) {
            return;
          }
          then();
        },
      },
    ]);
  };

  const editClasses = () => {
    // Start over from the server's `selected` (the store's list is cleared on reload).
    setIntent(null);
    setChecked(null);
    setStep('classes');
    dispatch(getTimeOffCandidates(requestId));
  };

  const openPicker = (event: TimeOffEvent, mode: 'assign' | 'record' = 'assign') => {
    setPicker({
      visible: true,
      eventId: event.id,
      currentSubId: event.sub?.employee.id ?? null,
      mode,
      eventDate: event.eventDate,
    });
  };

  const closePicker = () => setPicker((p) => ({ ...p, visible: false }));

  const doClearSub = (event: TimeOffEvent) => {
    dispatch(clearSub({ id: requestId, eventId: event.id })).then((result) => {
      if (clearSub.rejected.match(result)) {
        Alert.alert('Time Off', (result.payload as string) || 'Could not clear the sub.');
      }
    });
  };

  const onClearSub = (event: TimeOffEvent, recorded = false) => {
    if (!recorded) {
      doClearSub(event);
      return;
    }
    const sub = event.sub?.employee;
    Alert.alert(
      'Clear recorded sub?',
      `This removes the paid shift recorded for ${sub ? `${sub.firstName} ${sub.lastName}` : 'the sub'} on ${fmtDay(event.eventDate)}.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Clear', style: 'destructive', onPress: () => doClearSub(event) },
      ]
    );
  };

  const onSubmit = () => {
    const gaps = detail?.coverage?.gaps ?? 0;
    const total = detail?.coverage?.total ?? 0;
    if (gaps > 0) {
      Alert.alert(
        'Submit with gaps?',
        `${gaps} of ${total} classes have no sub — submit anyway? A manager will see the gaps.`,
        [
          { text: 'Keep editing', style: 'cancel' },
          { text: 'Submit anyway', onPress: () => finishSubmit(false) },
        ]
      );
    } else {
      finishSubmit(false);
    }
  };

  const data = step === 'classes' ? candidates : detail;
  // Reached from a "Pick your classes" reminder, the request may already be pending or
  // approved: editing classes is allowed, but there is nothing to submit — finish with DONE.
  const status = step === 'classes' ? candidates?.status : detail?.status;
  const alreadySubmitted = status != null && status !== 'CREATED';

  // LOADING
  if (!data && !error) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.accent} />
        <Text style={styles.centeredText}>
          {readOnly ? 'Loading…' : 'Finding your classes…'}
        </Text>
      </View>
    );
  }

  // 403 on the request itself: no longer on it (e.g. a sub tapping "Sub no longer needed").
  if (!data && step === 'subs' && detailErrorStatus === 403) {
    return (
      <View style={styles.centered}>
        <View style={styles.errorCard}>
          <Icon name="calendar-outline" size={30} color={colors.inkFaint} />
          <Text style={styles.errorTitle}>This request is no longer active</Text>
          <Text style={styles.errorBody}>
            You're no longer part of this time-off request — the sub may have been changed or the
            request was cancelled.
          </Text>
          <TouchableOpacity
            style={styles.retryBtn}
            activeOpacity={0.85}
            onPress={() => (navigation.canGoBack?.() ? navigation.goBack() : navigation.navigate('ScheduleHome'))}>
            <Text style={styles.retryText}>DONE</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // ERROR (surfaced, not a silent empty list)
  if (!data) {
    return (
      <View style={styles.centered}>
        <View style={styles.errorCard}>
          <Icon name="cloud-offline-outline" size={30} color={colors.accentDark} />
          <Text style={styles.errorTitle}>Could not load your classes</Text>
          <Text style={styles.errorBody}>{error}</Text>
          <TouchableOpacity style={styles.retryBtn} activeOpacity={0.85} onPress={load}>
            <Text style={styles.retryText}>RETRY</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const header = (kicker: string) => (
    <View style={styles.header}>
      <Text style={styles.kicker}>{kicker}</Text>
      <Text style={styles.headline}>{fmtRange(data.start, data.end)}</Text>
      <View style={styles.rule} />
      {step === 'subs' && !!detail?.notes && <Text style={styles.notes}>{detail.notes}</Text>}
    </View>
  );

  // --- Step 1: which classes need a sub -------------------------------------
  if (step === 'classes' && candidates) {
    const list = candidates.candidates;
    const marks = checked ?? new Set<number>();
    const checkedCount = list.filter((c) => marks.has(c.id)).length;
    const published = fmtShortDate(candidates.schedulePublishedThrough);

    return (
      <>
        <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
          {header('WHICH CLASSES NEED A SUB')}

          {list.length === 0 ? (
            <View style={styles.empty}>
              <Icon name="calendar-outline" size={34} color={colors.inkFaint} />
              <Text style={styles.emptyTitle}>No classes yet</Text>
              <Text style={styles.emptyBody}>
                {`Your schedule isn't published for these dates yet${
                  published ? ` (published through ${published})` : ''
                }. You can still submit your time off.`}
              </Text>
            </View>
          ) : (
            <>
              <Text style={styles.instructions}>Uncheck any class you'll still teach.</Text>
              <Text style={styles.note}>Future classes are based on the current schedule.</Text>
              {groups.map((g) => (
                <View key={g.date} style={styles.dayGroup}>
                  <View style={styles.dayHeadingRow}>
                    <Text style={styles.dayHeading}>{fmtDayHeading(g.date)}</Text>
                    {isTentativeDate(g.date) && <Text style={styles.tentativeTag}>Tentative</Text>}
                  </View>
                  {g.items.map((c) => {
                    const isChecked = marks.has(c.id);
                    const locked = !canToggle(c);
                    const reason = c.lockedReason ? LOCKED_COPY[c.lockedReason] : null;
                    return (
                      <TouchableOpacity
                        key={c.id}
                        style={[styles.checkRow, locked && styles.checkRowLocked]}
                        activeOpacity={0.7}
                        disabled={locked}
                        onPress={() => toggle(c)}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: isChecked, disabled: locked }}
                        accessibilityLabel={`${c.name}, ${c.start}`}>
                        <Icon
                          name={isChecked ? 'checkbox' : 'square-outline'}
                          size={26}
                          color={locked ? colors.inkFaint : colors.accent}
                        />
                        <View style={styles.checkText}>
                          <Text style={styles.checkName} numberOfLines={1}>
                            {c.name}
                          </Text>
                          <Text style={styles.eventMeta}>
                            {c.start}–{c.end}
                            {c.dept ? ` · ${c.dept}` : ''}
                          </Text>
                          {!!reason && <Text style={styles.lockedText}>{reason}</Text>}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ))}
            </>
          )}
        </ScrollView>

        <View style={styles.footer}>
          {alreadySubmitted && list.length === 0 ? (
            <TouchableOpacity style={styles.submit} activeOpacity={0.85} onPress={done}>
              <Text style={styles.submitText}>DONE</Text>
            </TouchableOpacity>
          ) : alreadySubmitted && checkedCount === 0 ? (
            <TouchableOpacity
              style={[styles.submit, isSavingClasses && styles.submitBusy]}
              activeOpacity={0.85}
              disabled={isSavingClasses}
              onPress={() =>
                confirmNoClasses(
                  'No subs needed?',
                  "You'll teach all of these classes yourself — they'll be removed from this request.",
                  saveClasses,
                  done,
                  'Save'
                )
              }>
              <Text style={styles.submitText}>{isSavingClasses ? 'SAVING…' : 'SAVE — NO SUBS NEEDED'}</Text>
            </TouchableOpacity>
          ) : list.length === 0 ? (
            <TouchableOpacity
              style={[styles.submit, isSubmitting && styles.submitBusy]}
              activeOpacity={0.85}
              disabled={isSubmitting}
              onPress={() =>
                confirmNoClasses(
                  'Submit without classes?',
                  "You don't have any classes on these dates yet. Submit your time off anyway?"
                )
              }>
              <Text style={styles.submitText}>{isSubmitting ? 'SUBMITTING…' : 'SUBMIT REQUEST'}</Text>
            </TouchableOpacity>
          ) : checkedCount === 0 ? (
            <TouchableOpacity
              style={[styles.submit, (isSubmitting || isSavingClasses) && styles.submitBusy]}
              activeOpacity={0.85}
              disabled={isSubmitting || isSavingClasses}
              onPress={() =>
                confirmNoClasses(
                  'No subs needed?',
                  "You'll teach all of these classes yourself. Submit your time off?",
                  saveClasses
                )
              }>
              <Text style={styles.submitText}>SUBMIT — NO SUBS NEEDED</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={[styles.submit, isSavingClasses && styles.submitBusy]}
              activeOpacity={0.85}
              disabled={isSavingClasses}
              onPress={onNext}>
              <Text style={styles.submitText}>
                {isSavingClasses
                  ? 'SAVING…'
                  : `NEXT: PICK SUBS (${checkedCount})`}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </>
    );
  }

  if (!detail) {
    return null;
  }

  // --- Step 2 (and read-only): who covers each class --------------------------
  const events = detail.events ?? [];
  const coverage = detail.coverage;
  const isEmpty = events.length === 0;
  // Who may change subs here: the editable flows (own draft / "pick your classes"), or —
  // on the detail screen — the requester or a manager while the request is still live.
  const canManageSubs =
    !readOnly ||
    (EDITABLE_STATUSES.has(detail.status) && (viewerIsAdmin || viewer?.id === detail.employee?.id));
  const now = new Date();

  return (
    <>
      <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
        {header(readOnly ? 'TIME OFF' : 'WHO COVERS YOU')}

        {!readOnly && (
          <TouchableOpacity style={styles.editLink} activeOpacity={0.7} onPress={editClasses}>
            <Icon name="create-outline" size={16} color={colors.accentDark} />
            <Text style={styles.clearLinkText}>Change which classes need a sub</Text>
          </TouchableOpacity>
        )}

        {!isEmpty && coverage && (
          <Text style={styles.coverageLine}>
            {coverage.covered} of {coverage.total} classes covered
            {coverage.gaps > 0 ? ` · ${coverage.gaps} still open` : ''}
          </Text>
        )}

        {isEmpty ? (
          <View style={styles.empty}>
            <Icon name="calendar-outline" size={34} color={colors.inkFaint} />
            <Text style={styles.emptyTitle}>No classes need a sub</Text>
            <Text style={styles.emptyBody}>
              None of your classes on these dates are marked as needing a sub.
            </Text>
          </View>
        ) : (
          events.map((event) => {
            const chip = coverageChip(event);
            const assigned = event.coverageState === 'assigned' || event.coverageState === 'accepted';
            const declined = declineLine(event);
            const row = (
              <>
                <View style={styles.cardHead}>
                  <View style={styles.cardHeadText}>
                    <Text style={styles.eventName} numberOfLines={1}>
                      {event.name}
                    </Text>
                    <Text style={styles.eventMeta}>
                      {fmtEventDate(event.eventDate)} · {event.start}–{event.end}
                      {event.dept ? ` · ${event.dept}` : ''}
                    </Text>
                  </View>
                  <View style={[styles.chip, { backgroundColor: chip.bg }]}>
                    <Text style={[styles.chipText, { color: chip.color }]} numberOfLines={1}>
                      {chip.label}
                    </Text>
                  </View>
                </View>
                {!!declined && <Text style={styles.declinedText}>{declined}</Text>}
              </>
            );

            if (!canManageSubs) {
              return (
                <View
                  key={event.id}
                  style={[styles.card, event.coverageState === 'unassigned' && styles.cardGap]}>
                  {row}
                </View>
              );
            }

            const action = subActionFor(event.eventDate, viewerIsAdmin, now);
            if (action === 'askManager' || action === 'tooOld') {
              return (
                <View key={event.id} style={styles.card}>
                  {row}
                  <Text style={styles.subNote}>
                    {action === 'askManager'
                      ? 'Ask a manager to record a past sub.'
                      : "More than 31 days ago — can't be changed here."}
                  </Text>
                </View>
              );
            }

            const recording = action === 'record';
            return (
              <TouchableOpacity
                key={event.id}
                style={styles.card}
                activeOpacity={0.7}
                onPress={() => openPicker(event, recording ? 'record' : 'assign')}
                accessibilityHint={recording ? 'Record a sub for a past class' : undefined}>
                {row}
                {assigned && (
                  <TouchableOpacity
                    style={styles.clearLink}
                    activeOpacity={0.7}
                    onPress={() => onClearSub(event, recording)}>
                    <Icon name="close-circle-outline" size={16} color={colors.accentDark} />
                    <Text style={styles.clearLinkText}>Clear sub</Text>
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            );
          })
        )}
      </ScrollView>

      {!readOnly && (
        <View style={styles.footer}>
          {alreadySubmitted ? (
            <TouchableOpacity style={styles.submit} activeOpacity={0.85} onPress={done}>
              <Text style={styles.submitText}>DONE</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={[styles.submit, isSubmitting && styles.submitBusy]}
              activeOpacity={0.85}
              disabled={isSubmitting}
              onPress={onSubmit}>
              <Text style={styles.submitText}>
                {isSubmitting ? 'SUBMITTING…' : 'SUBMIT REQUEST'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {canManageSubs && (
        <SubPickerModal
          visible={picker.visible}
          requestId={requestId}
          eventId={picker.eventId}
          currentSubId={picker.currentSubId}
          mode={picker.mode}
          eventDate={picker.eventDate}
          onClose={closePicker}
          onAssigned={() => dispatch(getTimeOffDetail(requestId))}
        />
      )}
    </>
  );
};

type AssembleRoute = RouteProp<ScheduleStackParamList, 'TimeOffAssemble'>;

/** Editable assemble screen — pick classes, then subs, then submit the draft. */
const AssembleTimeOffScreen: React.FC = () => {
  const route = useRoute<AssembleRoute>();
  return <TimeOffAssembleView requestId={route.params.requestId} />;
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  content: { padding: space.lg, paddingBottom: space.xxl },

  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.bg,
    padding: space.lg,
  },
  centeredText: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    marginTop: space.md,
  },

  header: { marginBottom: space.lg },
  kicker: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 2.5,
    color: colors.accent,
    marginBottom: -2,
  },
  headline: {
    fontFamily: font.display,
    fontSize: 44,
    lineHeight: 46,
    color: colors.ink,
    letterSpacing: -1,
  },
  rule: {
    height: 3,
    width: 48,
    backgroundColor: colors.ink,
    borderRadius: 2,
    marginTop: space.sm,
  },
  notes: {
    fontFamily: font.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.inkSoft,
    marginTop: space.md,
  },

  coverageLine: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 0.4,
    color: colors.inkSoft,
    marginBottom: space.md,
  },

  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.lg,
    marginBottom: space.md,
    borderWidth: 1,
    borderColor: colors.line,
  },
  cardGap: {
    borderColor: '#E8D49B',
    ...shadow.card,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardHeadText: { flex: 1, marginRight: space.sm },
  eventName: {
    fontFamily: font.displayBold,
    fontSize: 20,
    color: colors.ink,
  },
  eventMeta: {
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.inkSoft,
    marginTop: 2,
  },
  chip: {
    borderRadius: radius.pill,
    paddingVertical: 5,
    paddingHorizontal: 10,
    maxWidth: 140,
  },
  chipText: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 0.4,
  },
  clearLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: space.md,
    alignSelf: 'flex-start',
  },
  clearLinkText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.accentDark,
    marginLeft: 4,
  },

  empty: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.xl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.line,
    marginBottom: space.md,
  },
  emptyTitle: {
    fontFamily: font.displayBold,
    fontSize: 22,
    color: colors.ink,
    marginTop: space.sm,
  },
  emptyBody: {
    fontFamily: font.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.inkSoft,
    textAlign: 'center',
    marginTop: space.xs,
  },

  errorCard: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.xl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.line,
    alignSelf: 'stretch',
  },
  errorTitle: {
    fontFamily: font.displayBold,
    fontSize: 22,
    color: colors.ink,
    marginTop: space.sm,
  },
  errorBody: {
    fontFamily: font.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.inkSoft,
    textAlign: 'center',
    marginTop: space.xs,
    marginBottom: space.lg,
  },
  retryBtn: {
    height: 48,
    paddingHorizontal: space.xl,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  retryText: {
    fontFamily: font.display,
    fontSize: 20,
    letterSpacing: 1,
    color: colors.onAccent,
  },

  footer: {
    padding: space.lg,
    borderTopWidth: 1,
    borderTopColor: colors.line,
    backgroundColor: colors.bg,
  },
  submit: {
    height: 56,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitBusy: { opacity: 0.5 },

  instructions: {
    fontFamily: font.bodySemi,
    fontSize: 16,
    color: colors.ink,
    marginBottom: space.md,
  },
  note: {
    fontFamily: font.body,
    fontSize: 12,
    color: colors.inkSoft,
    marginTop: -space.sm,
    marginBottom: space.md,
  },
  dayGroup: { marginBottom: space.lg },
  dayHeadingRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  tentativeTag: {
    fontFamily: font.bodySemi,
    fontSize: 12,
    color: colors.inkFaint,
    marginBottom: space.sm,
  },
  dayHeading: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 1.5,
    color: colors.inkSoft,
    textTransform: 'uppercase',
    marginBottom: space.sm,
  },
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    paddingVertical: space.md,
    paddingHorizontal: space.lg,
    marginBottom: space.sm,
    borderWidth: 1,
    borderColor: colors.line,
    minHeight: 56,
  },
  checkRowLocked: { opacity: 0.6 },
  checkText: { flex: 1, marginLeft: space.md },
  checkName: {
    fontFamily: font.displayBold,
    fontSize: 18,
    color: colors.ink,
  },
  lockedText: {
    fontFamily: font.bodySemi,
    fontSize: 12,
    color: colors.accentDark,
    marginTop: 2,
  },
  subNote: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.inkSoft,
    marginTop: space.sm,
  },
  declinedText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.warn,
    marginTop: space.sm,
  },
  editLink: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: space.md,
  },
  submitText: {
    fontFamily: font.display,
    fontSize: 22,
    letterSpacing: 1,
    color: colors.onAccent,
  },
});

export default AssembleTimeOffScreen;
